import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';

// The review of a person's co-authored Austrian studies (design study-feed §6.2, S6), the same list on
// the person's own profile and on the admin's person page: HEARTPORT EXPERT's suggestions by the
// person's name with "Mine" / "Not mine", the confirmed studies and the rejected ones, each with
// "Undo". A study that names the person in full counts as confirmed without a decision (automatic,
// S6 as amended 2026-10-24); it offers "Not mine" instead of "Undo". A study shows its original title, journal, Expert's date and the authors, which is how an
// author recognises their paper. onDecide(pmid, decision) answers a promise; decision is CONFIRMED,
// REJECTED or null (undo). admin: the admin's wording ("Confirm", "Not this person"), and who decided.

const AUTHORS_SHOWN = 6;

const Section = styled.div`& + &{margin-top:1rem;}`;
const Heading = styled.div`font-size:.72rem;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:#5B6B78;margin-bottom:.4rem;`;
const List = styled.ul`list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:.5rem;`;
const Item = styled.li`background:#fff;border:1px solid #dbe3ea;padding:.7rem .8rem;`;
const StudyTitle = styled.a`display:block;font-weight:700;color:#111D4E;font-size:.9rem;line-height:1.35;text-decoration:none;&:hover{text-decoration:underline;}`;
const Meta = styled.div`color:#5B6B78;font-size:.78rem;margin-top:.2rem;`;
const Authors = styled.div`color:#334155;font-size:.78rem;margin-top:.2rem;`;
const Actions = styled.div`display:flex;flex-wrap:wrap;gap:.5rem;margin-top:.5rem;align-items:center;`;
const Action = styled.button`
  border:1px solid ${(p) => (p.$primary ? '#47ABDB' : '#cbd5e1')};background:${(p) => (p.$primary ? '#47ABDB' : '#fff')};
  color:${(p) => (p.$primary ? '#fff' : '#334155')};font-weight:700;font-size:.78rem;padding:.3rem .7rem;cursor:pointer;
  &:disabled{opacity:.5;cursor:not-allowed;}
`;
const Decided = styled.span`color:#5B6B78;font-size:.75rem;`;
const Toggle = styled.button`background:none;border:0;padding:0;color:#0369a1;font-weight:600;font-size:.8rem;cursor:pointer;&:hover{text-decoration:underline;}`;
const Error = styled.p`color:#b91c1c;font-size:.8rem;margin:.4rem 0 0;`;
const Quiet = styled.p`color:#5B6B78;font-size:.85rem;margin:0;`;

const StudyClaimsList = ({ claims, onDecide, admin = false }) => {
  const intl = useIntl();
  const t = (id, defaultMessage, values) => intl.formatMessage({ id, defaultMessage }, values);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [showRejected, setShowRejected] = useState(false);

  const decide = (pmid, decision) => {
    setBusy(pmid);
    setError(null);
    Promise.resolve(onDecide(pmid, decision))
      .catch(() => setError(t('studyClaims.error', 'The decision could not be saved. Please try again.')))
      .finally(() => setBusy(null));
  };

  const decidedBy = (study) => {
    if (study.automatic) {
      return admin
        ? t('studyClaims.adminAutomatic', 'Found by the full name')
        : t('studyClaims.automatic', 'Found by your full name');
    }
    if (!study.decidedAs) return null;
    if (admin && study.decidedBy) return t('studyClaims.decidedBy', 'Decided by {name}', { name: study.decidedBy });
    return study.decidedAs === 'ADMIN'
      ? t('studyClaims.decidedByAdmin', 'Decided by the organizers')
      : t('studyClaims.decidedBySelf', 'Decided by the person');
  };

  const item = (study, actions) => {
    const authors = study.authors || [];
    return (
      <Item key={study.pmid}>
        <StudyTitle href={study.url} target="_blank" rel="noreferrer">{study.title}</StudyTitle>
        <Meta>{[study.journalName, study.publicationDate].filter(Boolean).join(' · ')}</Meta>
        {authors.length > 0 && (
          <Authors>
            {authors.slice(0, AUTHORS_SHOWN).join(', ')}
            {authors.length > AUTHORS_SHOWN && ` ${t('studyClaims.moreAuthors', '+{count} more', { count: authors.length - AUTHORS_SHOWN })}`}
          </Authors>
        )}
        <Actions>{actions}</Actions>
      </Item>
    );
  };

  const suggestions = claims?.suggestions || [];
  const confirmed = claims?.confirmed || [];
  const rejected = claims?.rejected || [];
  if (suggestions.length + confirmed.length + rejected.length === 0) {
    return <Quiet>{t('studyClaims.none', 'HEARTPORT EXPERT has found no Austrian study by this name.')}</Quiet>;
  }

  return (
    <div>
      {suggestions.length > 0 && (
        <Section>
          <Heading>
            {admin
              ? t('studyClaims.adminSuggestions', 'Found by the name ({count})', { count: suggestions.length })
              : t('studyClaims.suggestions', 'Possibly yours ({count})', { count: suggestions.length })}
          </Heading>
          <List>
            {suggestions.map((s) => item(s, (
              <>
                <Action type="button" $primary disabled={busy === s.pmid} onClick={() => decide(s.pmid, 'CONFIRMED')}>
                  {admin ? t('studyClaims.adminConfirm', 'Confirm') : t('studyClaims.confirm', 'Mine')}
                </Action>
                <Action type="button" disabled={busy === s.pmid} onClick={() => decide(s.pmid, 'REJECTED')}>
                  {admin ? t('studyClaims.adminReject', 'Not this person') : t('studyClaims.reject', 'Not mine')}
                </Action>
              </>
            )))}
          </List>
        </Section>
      )}
      {confirmed.length > 0 && (
        <Section>
          <Heading>{t('studyClaims.confirmed', 'Confirmed ({count})', { count: confirmed.length })}</Heading>
          <List>
            {confirmed.map((s) => item(s, (
              <>
                {s.automatic ? (
                  <Action type="button" disabled={busy === s.pmid} onClick={() => decide(s.pmid, 'REJECTED')}>
                    {admin ? t('studyClaims.adminReject', 'Not this person') : t('studyClaims.reject', 'Not mine')}
                  </Action>
                ) : (
                  <Action type="button" disabled={busy === s.pmid} onClick={() => decide(s.pmid, null)}>
                    {t('studyClaims.undo', 'Undo')}
                  </Action>
                )}
                <Decided>{decidedBy(s)}</Decided>
              </>
            )))}
          </List>
        </Section>
      )}
      {rejected.length > 0 && (
        <Section>
          <Toggle type="button" aria-expanded={showRejected} onClick={() => setShowRejected(!showRejected)}>
            {admin
              ? t('studyClaims.adminRejected', 'Not this person ({count})', { count: rejected.length })
              : t('studyClaims.rejected', 'Not mine ({count})', { count: rejected.length })}
          </Toggle>
          {showRejected && (
            <List style={{ marginTop: '.4rem' }}>
              {rejected.map((s) => item(s, (
                <>
                  <Action type="button" disabled={busy === s.pmid} onClick={() => decide(s.pmid, null)}>
                    {t('studyClaims.undo', 'Undo')}
                  </Action>
                  <Decided>{decidedBy(s)}</Decided>
                </>
              )))}
            </List>
          )}
        </Section>
      )}
      {error && <Error role="alert">{error}</Error>}
    </div>
  );
};

export default StudyClaimsList;
